import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";

export const Route = createFileRoute("/courses")({
  head: () => ({
    meta: [
      { title: "Courses — Code Cubs Academy" },
      {
        name: "description",
        content: "Scratch, Python, Web Dev, AI, Mobile, Roblox — tracks for every age and level.",
      },
    ],
  }),
  component: CoursesPage,
});

interface DBCourse {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  age_min: number;
  age_max: number;
  duration_weeks: number;
  level: string;
  price: number;
  symbol: string | null;
  skills: string[] | null;
}

function CoursesPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<DBCourse[] | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [activeCourse, setActiveCourse] = useState<DBCourse | null>(null);
  const [busy, setBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");

  useEffect(() => {
    supabase
      .from("courses")
      .select("*")
      .eq("is_published", true)
      .order("sort_order")
      .then(({ data, error }) => {
        if (error) toast.error(error.message);
        setCourses((data ?? []) as DBCourse[]);
      });
  }, []);

  const filteredCourses =
    courses?.filter((c) => {
      const matchesSearch =
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.description && c.description.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesLevel = levelFilter === "all" || c.level === levelFilter;
      return matchesSearch && matchesLevel;
    }) ?? [];

  const startEnroll = (course: DBCourse) => {
    if (!user) {
      toast.info("سجل دخولك الأول عشان تقدر تسجل في الكورس");
      navigate({ to: "/login" });
      return;
    }
    setActiveCourse(course);
    setEnrollOpen(true);
  };

  const submitEnroll = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!activeCourse || !user) return;
    const fd = new FormData(e.currentTarget);
    const child_name = String(fd.get("child_name") ?? "").trim();
    const child_age = Number(fd.get("child_age"));
    const notes = String(fd.get("notes") ?? "").trim() || null;
    if (child_name.length < 2 || !child_age || child_age < 4 || child_age > 18) {
      toast.error("بيانات الطفل غير صحيحة");
      return;
    }
    setBusy(true);
    const { error } = await supabase.from("enrollments").insert({
      user_id: user.id,
      course_id: activeCourse.id,
      child_name,
      child_age,
      notes,
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("تم التسجيل! هنتواصل معاك قريباً 🎉");
    setEnrollOpen(false);
    setActiveCourse(null);
  };

  return (
    <div>
      <section className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-4xl lg:text-6xl font-extrabold">{t.courses.title}</h1>
        <p className="mt-3 text-lg text-muted-foreground max-w-2xl mx-auto">{t.courses.desc}</p>
      </section>

      <section className="container mx-auto px-4 pb-20">
        <div className="flex flex-col md:flex-row gap-4 mb-8 max-w-3xl mx-auto">
          <Input
            placeholder="بحث عن كورس..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 rounded-2xl h-12 px-5 bg-surface"
          />
          <select
            className="flex h-12 w-full md:w-48 rounded-2xl border border-input bg-surface px-5 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
          >
            <option value="all">جميع المستويات</option>
            <option value="Beginner">مبتدئ (Beginner)</option>
            <option value="Intermediate">متوسط (Intermediate)</option>
            <option value="Advanced">متقدم (Advanced)</option>
          </select>
        </div>

        {courses === null ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredCourses.length === 0 ? (
          <p className="text-center text-muted-foreground">مفيش كورسات متاحة حالياً تطابق بحثك</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredCourses.map((c) => (
              <Card
                key={c.id}
                className="border-0 shadow-soft hover:shadow-playful transition rounded-3xl overflow-hidden"
              >
                <div className="h-36 bg-gradient-primary flex items-center justify-center text-6xl text-primary-foreground">
                  {c.symbol ?? "💻"}
                </div>
                <CardContent className="p-6 space-y-3">
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="secondary" className="rounded-full">
                      {t.courses.ageLabel}: {c.age_min}-{c.age_max}
                    </Badge>
                    <Badge variant="outline" className="rounded-full">
                      {c.level}
                    </Badge>
                    <Badge variant="outline" className="rounded-full">
                      {c.duration_weeks}w
                    </Badge>
                  </div>
                  <h2 className="font-bold text-xl">{c.name}</h2>
                  <p className="text-sm text-muted-foreground line-clamp-3">{c.description}</p>
                  <div className="flex items-center justify-between pt-2">
                    <span className="font-extrabold text-lg">
                      {c.price > 0 ? `${c.price} EGP` : "Free"}
                    </span>
                  </div>
                  <Button
                    onClick={() => startEnroll(c)}
                    className="rounded-full w-full bg-gradient-primary text-primary-foreground border-0"
                  >
                    {t.cta.enroll}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>التسجيل في {activeCourse?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEnroll} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="child_name">اسم الطفل</Label>
              <Input id="child_name" name="child_name" required maxLength={80} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="child_age">سن الطفل</Label>
              <Input id="child_age" name="child_age" type="number" min={4} max={18} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">ملاحظات (اختياري)</Label>
              <Textarea id="notes" name="notes" rows={3} maxLength={500} />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={busy}
                className="rounded-full bg-gradient-primary text-primary-foreground border-0"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "أكد التسجيل"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
